import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  OnDestroy,
  signal,
  type Signal,
  type WritableSignal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ThemeService } from "@shared/services/theme/theme.service";

@Component({
  selector: "app-excuela-carousel",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./excuela-carousel.component.html",
  styleUrls: ["./excuela-carousel.component.scss"],
  imports: [CommonModule, TranslateModule],
})
export class ExcuelaCarouselComponent implements OnInit, OnDestroy {
  /**
   * El indice avanza desde `setInterval` y `setTimeout`, fuera de cualquier
   * evento de Angular. Como senal, escribirlo basta para que la vista se
   * repinte con OnPush y sin zone.
   */
  private readonly _currentIndex: WritableSignal<number> = signal<number>(0);
  readonly currentIndex: Signal<number> = this._currentIndex.asReadonly();

  readonly transform: Signal<string> = computed((): string => `translateX(${-this.currentIndex() * 100}%)`);

  /** Solo bloquea el avance mientras dura la transicion; no se pinta. */
  private isTransitioning: boolean = false;
  private autoSlideInterval: ReturnType<typeof setInterval> | undefined;

  readonly textPresentations: ReadonlyArray<{ lowerTitle: string; bottomStoryText: string }> = [
    { lowerTitle: "excuelaCarousel.lowerTitle1", bottomStoryText: "excuelaCarousel.bottomStoryText1" },
    { lowerTitle: "excuelaCarousel.lowerTitle2", bottomStoryText: "excuelaCarousel.bottomStoryText2" },
    { lowerTitle: "excuelaCarousel.lowerTitle3", bottomStoryText: "excuelaCarousel.bottomStoryText3" },
    { lowerTitle: "excuelaCarousel.lowerTitle4", bottomStoryText: "excuelaCarousel.bottomStoryText4" },
  ];

  readonly themeService: ThemeService = inject(ThemeService);

  ngOnInit(): void {
    this.startAutoSlide();
  }

  ngOnDestroy(): void {
    this.stopAutoSlide();
  }

  prevSlide(): void {
    this.slideBy(-1);
  }

  nextSlide(): void {
    this.slideBy(1);
  }

  goToSlide(index: number): void {
    this._currentIndex.set(index);
  }

  private slideBy(step: number): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    setTimeout((): void => {
      const total: number = this.themeService.image().carousel.length;
      if (total > 0) {
        this._currentIndex.update((index: number): number => (index + step + total) % total);
      }
      this.isTransitioning = false;
    }, 500);
  }

  private startAutoSlide(): void {
    this.autoSlideInterval = setInterval((): void => {
      this.nextSlide();
    }, 3000);
  }

  private stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }
}
